import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ChevronDown, User, LogOut, LayoutDashboard } from "lucide-react";
import { navLinks, getDashboardPath } from "./navConfig";

const DesktopNav = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  const toggleSection = (name, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSections((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const renderSubItems = (child) => {
    if (!child.children) {
      return (
        <DropdownMenuItem key={child.path} asChild>
          <Link
            to={child.path}
            className="w-full cursor-pointer rounded-lg px-3 py-2.5 hover:bg-rnb-light transition-colors"
            data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {child.name}
          </Link>
        </DropdownMenuItem>
      );
    }

    return (
      <div key={child.path || child.name}>
        {child.collapsible ? (
          <>
            <div
              className="w-full cursor-pointer rounded-lg px-3 py-2.5 hover:bg-rnb-light transition-colors text-sm font-semibold text-rnb-navy flex items-center justify-between"
              onClick={(e) => toggleSection(child.name, e)}
              onDoubleClick={() => { if (child.path) navigate(child.path); }}
              data-testid={`nav-toggle-${child.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {child.name}
              <ChevronDown className={`h-4 w-4 text-rnb-gray transition-transform duration-200 ${expandedSections[child.name] ? "rotate-180" : ""}`} />
            </div>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections[child.name] ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
              {child.children.map((sub) => (
                <DropdownMenuItem key={sub.path} asChild>
                  <Link
                    to={sub.path}
                    className="w-full cursor-pointer rounded-lg pl-7 pr-3 py-2 hover:bg-rnb-light transition-colors text-sm text-rnb-gray"
                    data-testid={`nav-${sub.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {sub.name}
                  </Link>
                </DropdownMenuItem>
              ))}
            </div>
            <DropdownMenuSeparator className="my-1" />
          </>
        ) : (
          <>
            <DropdownMenuItem asChild>
              <Link
                to={child.path}
                className="w-full cursor-pointer rounded-lg px-3 py-2.5 hover:bg-rnb-light transition-colors font-semibold text-rnb-navy"
                data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {child.name}
              </Link>
            </DropdownMenuItem>
            {child.children.map((sub) => (
              <DropdownMenuItem key={sub.path} asChild>
                <Link
                  to={sub.path}
                  className="w-full cursor-pointer rounded-lg pl-7 pr-3 py-2 hover:bg-rnb-light transition-colors text-sm text-rnb-gray"
                  data-testid={`nav-${sub.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {sub.name}
                </Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="my-1" />
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="hidden md:flex items-center space-x-1">
        {navLinks.map((link) =>
          link.children ? (
            <DropdownMenu key={link.name}>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center px-4 py-2 text-sm font-medium text-rnb-gray hover:text-rnb-blue transition-all duration-200 rounded-lg hover:bg-rnb-light"
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.name}
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-64 p-2 bg-white/95 backdrop-blur-lg shadow-card border border-gray-100">
                {link.children.map(renderSubItems)}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : link.external ? (
            <a
              key={link.path}
              href={link.path}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-rnb-gray hover:text-rnb-blue hover:bg-rnb-light transition-all duration-200 rounded-lg"
              data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {link.name}
            </a>
          ) : (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg ${
                isActive(link.path) ? "text-rnb-blue bg-rnb-light" : "text-rnb-gray hover:text-rnb-blue hover:bg-rnb-light"
              }`}
              data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {link.name}
            </Link>
          )
        )}
      </div>

      {/* Auth Buttons */}
      <div className="hidden md:flex items-center space-x-3">
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center space-x-2 rounded-full border-gray-200 hover:border-rnb-blue hover:bg-rnb-light transition-all duration-200"
                data-testid="user-menu-trigger"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span className="font-medium">{user.name?.split(" ")[0]}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 p-2 bg-white/95 backdrop-blur-lg shadow-card">
              <DropdownMenuItem asChild>
                <Link
                  to={getDashboardPath(user.role)}
                  className="flex items-center cursor-pointer rounded-lg px-3 py-2 hover:bg-rnb-light"
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                className="flex items-center cursor-pointer text-red-600 rounded-lg px-3 py-2 hover:bg-red-50"
                data-testid="nav-logout"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <>
            <Link to="/login">
              <Button variant="ghost" className="rounded-full hover:bg-rnb-light" data-testid="nav-login">
                Sign In
              </Button>
            </Link>
            <Link to="/consultation">
              <Button className="rounded-full bg-gradient-brand hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5" data-testid="nav-consultation">
                Book Consultation
              </Button>
            </Link>
          </>
        )}
      </div>
    </>
  );
};

export default DesktopNav;
